import Editor, { useMonaco } from '@monaco-editor/react';
import { Button, useToast } from '@serodigital/ui-components';
import * as React from 'react';
import * as schemaTwoDotOne from '../../../../../../bee-schemas/schemas/v2.1/schema.json';
import * as schemaTwoDotTwo from '../../../../../../bee-schemas/schemas/v2.2/schema.json';
import * as schemaTwoDotThree from '../../../../../../bee-schemas/schemas/v2.3/schema.json';
import * as schema from '../../../../../../bee-schemas/schemas/v2/schema.json';
import { BEEConfiguration } from '../../../../../shared/api-clients/_api';
import { getValidatedAuthToken } from '../../../../../shared/api-clients/auth0';

const versionCheck = (e: string, version) => e?.includes(`"schema_version": "${version}",`);

export function ConfigView({ deviceId }: { deviceId: string }) {
  const monaco = useMonaco();
  const { setToastMessage } = useToast();
  const [config, setConfig] = React.useState<BEEConfiguration>();

  const handleChange = (e: string) => {
    if (versionCheck(e, '2.3.0')) {
      monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
        validate: APP_CONFIG.environment !== 'dev',
        schemas: [
          {
            uri: 'schema.json',
            fileMatch: ['*'],
            schema: schemaTwoDotThree,
          },
        ],
      });
    }

    if (versionCheck(e, '2.2.0')) {
      monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
        validate: APP_CONFIG.environment !== 'dev',
        schemas: [
          {
            uri: 'schema.json',
            fileMatch: ['*'],
            schema: schemaTwoDotTwo,
          },
        ],
      });
    }

    if (versionCheck(e, '2.1.0')) {
      monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
        validate: APP_CONFIG.environment !== 'dev',
        schemas: [
          {
            uri: 'schema.json',
            fileMatch: ['*'],
            schema: schemaTwoDotOne,
          },
        ],
      });
    }

    if (versionCheck(e, '2.0.0')) {
      monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
        validate: APP_CONFIG.environment !== 'dev',
        schemas: [
          {
            uri: 'schema.json',
            fileMatch: ['*'],
            schema,
          },
        ],
      });
    }
  };

  React.useEffect(() => {
    if (!monaco) return;
    monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
      validate: true,
      schemas: [
        {
          uri: 'schema.json',
          fileMatch: ['*'],
          schema,
        },
      ],
    });
  }, [monaco]);

  React.useEffect(() => {
    handleChange(JSON.stringify(config, null, 4));
  }, [config]);

  React.useMemo(async () => {
    const authToken = await getValidatedAuthToken();
    const response = await fetch(`${APP_CONFIG.apiBaseUrl}/config?deviceId=${deviceId}`, {
      headers: { Authorization: `Bearer ${authToken}` },
    });
    setConfig(await response.json());
  }, []);

  const updateConfig = React.useCallback(async () => {
    const authToken = await getValidatedAuthToken();
    const response = await fetch(`${APP_CONFIG.apiBaseUrl}/config/replace`, {
      method: 'POST',
      body: JSON.stringify({ deviceId, data: JSON.parse(monaco.editor.getModels()[0].getValue()) }),
      headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      setToastMessage({
        type: 'error',
        label: `Failed to update config
        ${await response.text()}}`,
      });
    } else {
      setToastMessage({
        type: 'success',
        label: 'Successfully updated config',
      });
    }
  }, [config]);

  return (
    <div className="h-full flex flex-col" data-qa="heating-details">
      <Button className="mb-8 self-end" onClick={updateConfig}>
        Save
      </Button>
      <Editor onChange={(e) => handleChange(e)} height="50vh" language="json" value={JSON.stringify(config, null, 4)} />
    </div>
  );
}
